import React, { useState } from "react";
import { Copy, Mail, RotateCcw, Search, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { parApi, parErr } from "@/lib/partnerApi";
import { finApi } from "@/lib/financeApi";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { btn, btnGold, btnRed, Chip, Empty, ErrorBox, field, Loading, useLoad, when } from "@/components/nds/windows/finance/parts";

/** Send (or resend) the set-PIN invite; always shows the copyable one-time link (email may not deliver). */
export function InviteBox({ rec, reload }) {
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);
  if (rec.status === "inactive") return null;
  const send = async () => {
    setBusy(true);
    try { const d = await parApi.post(`/partners/${rec.id}/invite`); setInv(d); toast.success(d.email_sent ? "Invite emailed" : "Invite link ready — email not delivered"); reload(); }
    catch (e) { toast.error(parErr(e)); } finally { setBusy(false); }
  };
  const copy = () => navigator.clipboard?.writeText(inv.invite_url).then(() => toast.success("Link copied"), () => toast.error("Copy failed"));
  return (
    <div data-testid="par-invite-box" className="p-4 border border-white/10 rounded-sm space-y-2">
      <div className="flex items-center gap-3">
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Invite · {rec.invite_sent_at ? `last sent ${when(rec.invite_sent_at)}` : "not sent yet"}</span>
        <button type="button" disabled={busy} onClick={send} data-testid="par-invite-send" className={`${btnGold} ml-auto inline-flex items-center gap-1.5`}><Mail className="h-3 w-3" /> {rec.invite_sent_at ? "Resend invite" : "Send invite"}</button>
      </div>
      {inv && (
        <div className="flex items-center gap-2">
          <input readOnly value={inv.invite_url} data-testid="par-invite-url" className="flex-1 bg-black/40 border border-white/10 text-xs text-nex-silver px-2 py-1.5 rounded-sm font-mono" onFocus={(e) => e.target.select()} />
          <button type="button" onClick={copy} data-testid="par-invite-copy" aria-label="Copy invite link" className="p-1.5 text-nex-muted hover:text-nex-gold"><Copy className="h-3.5 w-3.5" /></button>
          <span data-testid="par-invite-email-status" className={`font-mono text-[9px] uppercase ${inv.email_sent ? "text-emerald-300" : "text-yellow-300"}`}>{inv.email_sent ? "emailed" : "email not sent"} · expires {inv.expires_at.slice(0, 16).replace("T", " ")}</span>
        </div>
      )}
    </div>
  );
}

function AssignPicker({ taken, onSave, onCancel }) {
  const [res, setRes] = useState(null);
  const [q, setQ] = useState("");
  const [pick, setPick] = useState([]);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { finApi.get("/residents", { status: "active" }).then((d) => setRes(d.residents)).catch(() => setRes([])); }, []);
  if (!res) return <Loading testid="par-asg-pick-loading" />;
  const list = res.filter((r) => !taken.has(r.id) && `${r.first_name} ${r.last_name}`.toLowerCase().includes(q.toLowerCase()));
  const flip = (id) => setPick((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const save = async () => { setBusy(true); await onSave(pick); setBusy(false); };
  return (
    <div data-testid="par-asg-picker" className="p-4 border border-nex-gold/30 rounded-sm space-y-3">
      <div className="relative"><Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-nex-muted" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search residents" data-testid="par-asg-search" className={`${field} w-full pl-8`} /></div>
      <div className="max-h-60 overflow-y-auto space-y-0.5">
        {list.length ? list.map((r) => (
          <label key={r.id} data-testid={`par-asg-opt-${r.id}`} className="flex items-center gap-2.5 px-2 py-1.5 rounded-sm hover:bg-white/5 cursor-pointer text-sm text-nex-silver">
            <Checkbox checked={pick.includes(r.id)} onCheckedChange={() => flip(r.id)} data-testid={`par-asg-check-${r.id}`} />{r.first_name} {r.last_name}
          </label>)) : <p className="text-xs text-nex-muted px-2 py-3">No unassigned residents match.</p>}
      </div>
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] text-nex-muted uppercase">{pick.length} selected</span>
        <button type="button" onClick={onCancel} data-testid="par-asg-cancel" className={`${btn} ml-auto border-white/15 text-nex-muted`}>Cancel</button>
        <button type="button" disabled={!pick.length || busy} onClick={save} data-testid="par-asg-save" className={btnGold}>{busy ? "Assigning…" : `Assign ${pick.length || ""}`}</button>
      </div>
    </div>
  );
}

export function PartnerAssignmentsTab({ rec }) {
  const [state, load] = useLoad(() => parApi.get("/assignments", { partner_id: rec.id }), [rec.id]);
  const [open, setOpen] = useState(false);
  const [ending, setEnding] = useState(null);
  if (state.loading && !state.data) return <Loading testid="par-dir-asg-loading" />;
  if (state.error) return <ErrorBox testid="par-dir-asg-error" error={state.error} onRetry={load} />;
  const rows = state.data.records;
  const active = rows.filter((a) => a.status === "active");
  const programWide = rec.type === "org_ambassador";
  const assign = async (ids) => {
    let ok = 0;
    for (const rid of ids) {
      try { await parApi.post("/assignments", { partner_id: rec.id, resident_id: rid }); ok += 1; } catch (e) { toast.error(parErr(e)); }
    }
    if (ok) toast.success(ok === 1 ? "Resident assigned" : `${ok} residents assigned`);
    setOpen(false); load();
  };
  const end = async (a) => {
    setEnding(a.id);
    try { await parApi.patch(`/assignments/${a.id}/end`, {}); toast.success("Assignment ended"); load(); } catch (e) { toast.error(parErr(e)); } finally { setEnding(null); }
  };
  const canAssign = rec.status !== "inactive";
  const hasWide = active.some((a) => !a.resident_id);
  return (
    <div className="p-5 space-y-4">
      {canAssign && !open && (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setOpen(true)} data-testid="par-asg-open" className={`${btnGold} inline-flex items-center gap-1.5`}>
            <UserPlus className="h-3 w-3" /> Assign residents
          </button>
          {programWide && !hasWide && (
            <button type="button" onClick={() => assign([null])} data-testid="par-asg-program-wide" className={`${btnGold} inline-flex items-center gap-1.5`}>Assign program-wide</button>
          )}
        </div>
      )}
      {open && <AssignPicker taken={new Set(active.map((a) => a.resident_id))} onSave={assign} onCancel={() => setOpen(false)} />}
      {!rows.length ? <Empty testid="par-dir-asg-empty">No residents assigned yet.</Empty> : (
        <ul data-testid="par-dir-asg-list" className="space-y-2">
          {rows.map((a) => (
            <li key={a.id} data-testid={`par-dir-asg-${a.id}`} className="flex items-center gap-3 text-sm">
              <span className="text-nex-silver">{a.resident_id ? a.resident_name : "Program-wide"}</span>
              <Chip tone={a.status === "active" ? "green" : "grey"}>{a.status}</Chip>
              <span className="font-mono text-[10px] text-nex-muted">since {a.assigned_at.slice(0, 10)}{a.end_date ? ` · ends ${a.end_date}` : ""}</span>
              {a.status === "active" && <button type="button" disabled={ending === a.id} onClick={() => end(a)} data-testid={`par-asg-end-${a.id}`} className={`${btnRed} ml-auto`}>Unassign</button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function PartnerPermissionsTab({ rec, reload }) {
  const [state, load] = useLoad(() => parApi.get(`/partners/${rec.id}/permissions`), [rec.id]);
  const [busy, setBusy] = useState(null);
  const set = async (key, granted) => {
    setBusy(key);
    try { await parApi.put(`/partners/${rec.id}/permissions/${key}`, { granted }); toast.success(granted === null ? "Reset to type default" : `${key} ${granted ? "granted" : "denied"}`); await load(); reload(); }
    catch (e) { toast.error(parErr(e)); } finally { setBusy(null); }
  };
  if (state.loading && !state.data) return <Loading testid="par-perm-loading" />;
  if (state.error) return <ErrorBox testid="par-perm-error" error={state.error} onRetry={load} />;
  return (
    <div data-testid="par-perm-tab" className="p-5 space-y-1">
      <p className="text-xs text-nex-muted pb-2">Overrides beat the type default. Resident data is still limited to assigned residents unless access_all_residents is granted.</p>
      {Object.entries(state.data.permissions).map(([key, p]) => (
        <div key={key} data-testid={`par-perm-${key}`} className="flex items-center gap-3 py-2 border-t border-white/5">
          <div className="min-w-0"><div className="text-sm text-nex-silver">{p.label}</div><div className="font-mono text-[10px] text-nex-muted">{key}</div></div>
          <Chip tone={p.source === "override" ? (p.granted ? "green" : "red") : "grey"} data-testid={`par-perm-${key}-source`}>{p.source === "override" ? "SA override" : "Type default"}</Chip>
          {p.source === "override" && <button type="button" disabled={busy === key} onClick={() => set(key, null)} data-testid={`par-perm-${key}-reset`} aria-label="Reset to default" className="text-nex-muted hover:text-nex-gold"><RotateCcw className="h-3.5 w-3.5" /></button>}
          <Switch className="ml-auto" checked={p.granted} disabled={busy === key} onCheckedChange={(v) => set(key, v)} data-testid={`par-perm-${key}-toggle`} />
        </div>
      ))}
    </div>
  );
}
